import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Tabs, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { NavigationTabList } from "./NavigationTabList";
import { NavigationTabPanels } from "./NavigationTabPanels";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { DeleteDialog } from "../../pages/DataEntryPlanning/components/DeleteDialog";
import { showErrorToast, showSuccessToast } from "../../utils";
import { useQueryClient } from "react-query";

function NavigationTabs({
                          elements,
                          tabTitle,
                          subTitleAttribute,
                          saveMethod,
                          customNavButton,
                          tabsContainerStyles = {},
                          tabsStyles = {},
                          buttonsPanelStyles = {},
                          navTabPanelStyles,
                          formSubmission,
                          deleteItemEntity,
                          showFinaliseButton = true
                        }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState(0);
  const [finalize, setFinalize] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const [requestedTab, setRequestedTab] = useState(null);
  const saveBtnRef = useRef();

  const tabsState = useBreakpointValue({ base: true, xl: false });
  const [tabsCollapsed, setTabsCollapsed] = useState(false);

  useEffect(() => {
    setTabsCollapsed(tabsState);
  }, [tabsState]);

  useEffect(() => {
    if (formSubmission?.allowed === true && formSubmission?.requested === true) {
      saveBtnRef.current.click();
    }
  }, [formSubmission?.allowed, formSubmission?.requested]);

  const handleTabsCollapse = (areCollapsed) => {
    setTabsCollapsed(areCollapsed);
  };

  const callMethod = (currentFormData) => {
    if (saveMethod) {
      if (currentFormData && currentFormData.isFormValid === false) {
        setRequestedTab(null);
        if (finalize) {
          setFinalize(false);
        }
        if (formSubmission && formSubmission.requested && formSubmission.decline) {
          formSubmission.decline();
        }
      } else {
        saveMethod(currentFormData, finalize, shouldNavigate);
        if (requestedTab !== null) {
          setTabIndex(requestedTab);
          setRequestedTab(null);
        }
        if (finalize) {
          setFinalize(false);
        }
      }
    }
    if (shouldNavigate) {
      setShouldNavigate(false);
    }
  };

  const [customButton, setCustomButton] = useState();

  useEffect(() => {
    if (customNavButton) {
      const button = { ...customNavButton };
      button.props = {
        ...button.props,
        form: `navForm${elements[tabIndex]?.id}`,
        onClick: () => setShouldNavigate(true)
      };
      setCustomButton(button);
    }
  }, [customNavButton, tabIndex, elements]);

  const handleFinalizeConfirmation = () => {
    const currentForm = document.getElementById(`navForm${elements[tabIndex]?.id}`);
    if (currentForm) {
      currentForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      handleDialogClose(true);
    }
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalize(false);
    }
    onConfirmDialogClose();
  };

  useEffect(() => {
    if (saveMethod && requestedTab !== null) {
      saveBtnRef.current.click();
    }
  }, [saveMethod, requestedTab]);

  const handleRequestTabChange = (requestedTabIndex) => {
    if (finalize) {
      setFinalize(false);
    }
    setRequestedTab(requestedTabIndex);
  };

  const handleItemDelete = () => {
    deleteItemEntity.deleteItem({
        id: elements[tabIndex]?.id
      },
      {
        onSuccess: () => {
          if (elements && elements.length > 0) {
            setTabIndex(0);
          }
          showSuccessToast(toast, deleteItemEntity.successMessage);
          queryClient.invalidateQueries(deleteItemEntity.queryKeys);
          onDeleteDialogClose();
        },
        onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
        cacheTime: 0
      });
  };

  return (
    <Box {...tabsContainerStyles}>
      <Tabs
        variant="unstyled"
        orientation="vertical"
        overflow="hidden"
        position="relative"
        isLazy
        isManual
        index={tabIndex}
        onChange={saveMethod ? null : setTabIndex}
        maxBlockSize={{ base: "none", lg: "lg" }}
        marginInlineEnd="-4"
        width="100%"
        {...tabsStyles}
      >
        <NavigationTabList
          currentIndex={tabIndex}
          elements={elements}
          tabsCollapsed={tabsCollapsed}
          setTabsCollapsed={handleTabsCollapse}
          tabTitle={tabTitle}
          subTitleAttribute={subTitleAttribute}
          requestTabChange={handleRequestTabChange}
          handleDeleteItem={deleteItemEntity ? onDeleteDialogOpen : null}
        />
        <NavigationTabPanels
          elements={elements}
          tabsCollapsed={tabsCollapsed}
          callMethod={saveMethod ? callMethod : null}
          tabTitle={tabTitle}
          navTabPanelStyles={navTabPanelStyles}
        />
      </Tabs>
      {saveMethod &&
        <>
          <Flex
            marginBlockStart="10"
            justifyContent={customNavButton ? "space-between" : "end"}
            position="sticky"
            bottom="0"
            backgroundColor="#fff"
            borderColor="esg_gray.100"
            bordersyle="solid"
            borderTopWidth="1px"
            zIndex="10"
            paddingBlock="4"
            {...buttonsPanelStyles}
          >
            {customButton}
            <Flex gap="2">
              <Button
                colorScheme={showFinaliseButton ? "white" : "customScheme"}
                color={showFinaliseButton ? "esg_lightgreen" : "white"}
                border={"1px solid"}
                borderColor={"esg_lightgreen"}
                form={`navForm${elements[tabIndex]?.id}`}
                type="submit"
                width="32"
                onClick={() => {
                  setShouldNavigate(false);
                  setFinalize(false);
                }}
                ref={saveBtnRef}
              >
                {t("buttons.save")}
              </Button>
              {showFinaliseButton ?
                <Button
                  form={`navForm${elements[tabIndex]?.id}`}
                  colorScheme="customScheme"
                  width="32"
                  onClick={() => {
                    setFinalize(true);
                    onConfirmDialogOpen();
                  }}
                >
                  {t("buttons.finalize")}
                </Button> : null
              }
            </Flex>
          </Flex>
          <ConfirmationDialog
            isOpen={isConfirmDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleFinalizeConfirmation}
            headerText={t("questionnaireFinalizationDialog.header")}
            confirmationText={t("questionnaireFinalizationDialog.question")}
          />
        </>
      }
      {deleteItemEntity ?
        <DeleteDialog
          isOpen={isDeleteDialogOpen}
          onClose={onDeleteDialogClose}
          onDelete={handleItemDelete}
          entityName={deleteItemEntity.entityName}
        /> : null
      }
    </Box>
  );
}

export default NavigationTabs;